/**
 * @fileoverview Helpers behind the shared "date calc" controls
 * @module components/settings/precedents/date-calc-utils
 *
 * Purpose:
 * - Decide which fields a date calc may start from, and which fields can be
 *   calculated at all (canonical date fields plus the org's custom ones).
 *
 * Split out from `date-calc-controls.tsx` so that file exports components
 * only, keeping Fast Refresh working for the editors that host it.
 *
 * Used by:
 * - src/components/settings/precedents/date-calc-controls.tsx
 * - src/components/settings/precedents/PdfFieldEditor.tsx
 * - src/ui/composed/editor/template-editor.tsx
 */

import {
  CANONICAL_FIELDS_MAP,
  FIELD_CATEGORIES,
} from "../../../../convex/settings/precedents/canonicalFields";
import type { DateCalc } from "../../../../convex/settings/precedents/dateCalc";

/** Minimal shape needed from an org custom field definition. */
export interface DateCalcCustomField {
  key: string;
  label: string;
  fieldType: string;
}

/** The calc a newly enabled field starts from, before the user adjusts it. */
export const DEFAULT_DATE_CALC: DateCalc = {
  baseKey: "appointment_date",
  amount: 1,
  unit: "years",
  direction: "before",
};

/**
 * Build the list of date fields a date calc can start from: every canonical
 * date field plus the org's custom date fields, excluding the field's own key
 * (a field can't derive from itself). Grouped by category label for
 * `<optgroup>` rendering.
 */
export function buildDateCalcBaseOptions(
  ownKey: string,
  customFields: DateCalcCustomField[],
): Array<{ group: string; options: Array<{ key: string; label: string }> }> {
  const groups: Array<{
    group: string;
    options: Array<{ key: string; label: string }>;
  }> = [];
  for (const cat of FIELD_CATEGORIES) {
    const options = cat.fields
      .filter((f) => f.fieldType === "date" && f.key !== ownKey)
      .map((f) => ({ key: f.key, label: f.label }));
    if (options.length > 0) groups.push({ group: cat.label, options });
  }
  const customDates = customFields
    .filter((f) => f.fieldType === "date" && f.key !== ownKey)
    .map((f) => ({ key: f.key, label: f.label }));
  if (customDates.length > 0) {
    groups.push({ group: "Custom Fields", options: customDates });
  }
  return groups;
}

/** Resolve a field key to its display label (canonical, then custom). */
export function dateCalcLabelForKey(
  key: string,
  customFields: DateCalcCustomField[],
): string | undefined {
  return (
    CANONICAL_FIELDS_MAP[key]?.label ??
    customFields.find((f) => f.key === key)?.label
  );
}

/** True when a field key names a date field (canonical or org custom). */
export function isDateFieldKey(
  key: string | null | undefined,
  customFields: DateCalcCustomField[],
): boolean {
  if (!key) return false;
  if (CANONICAL_FIELDS_MAP[key]?.fieldType === "date") return true;
  return customFields.some((f) => f.key === key && f.fieldType === "date");
}
